import { DataCollectionType } from 'lib_ts/enums/data-collection.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import { IInstallationDetails } from 'lib_ts/interfaces/i-machine';
import { ICollectionShotGroup } from 'lib_ts/interfaces/training/i-machine-shot';
import { DataCollectionsService } from 'services/data-collections.service';

export function collectionHasShots(summaries: ICollectionShotGroup[]): boolean {
  return summaries.some((g) => g.shots.length > 0);
}

function collectionMatchesBallType(
  existing: IDataCollection | undefined,
  ballType: BallType
): boolean {
  return !!existing && !existing.cancelled && existing.ball_type === ballType;
}

export interface EnsureInstallCollectionsArgs {
  machineID: string;
  ballType: BallType;
  installation: IInstallationDetails;
  createDataCollection: (
    type: DataCollectionType,
    machineID: string,
    ballType: BallType
  ) => Promise<IDataCollection | undefined>;
  existingBtc?: IDataCollection;
  existingRt?: IDataCollection;
  forceRecreateBtc?: boolean;
  forceRecreateRt?: boolean;
}

export interface EnsureInstallCollectionsResult {
  btcID: string | null;
  rtID: string | null;
  recreatedBtc: boolean;
  recreatedRt: boolean;
}

async function ensureCollection(
  type: DataCollectionType,
  machineID: string,
  ballType: BallType,
  existing: IDataCollection | undefined,
  createDataCollection: EnsureInstallCollectionsArgs['createDataCollection'],
  forceRecreate = false
): Promise<{ id: string | null; recreated: boolean }> {
  if (!forceRecreate && collectionMatchesBallType(existing, ballType)) {
    // no change needed
    return { id: existing?._id ?? null, recreated: false };
  }

  if (existing && !existing.complete && !existing.cancelled) {
    // cancel any existing, incomplete data collection
    await DataCollectionsService.updateOne(existing._id, {
      cancelled: true,
    });
  }

  // create a new data collection w/ the current ball type
  const created = await createDataCollection(type, machineID, ballType);

  return {
    id: created?._id ?? null,
    recreated: !!created,
  };
}

export async function ensureInstallationDataCollections(
  args: EnsureInstallCollectionsArgs
): Promise<EnsureInstallCollectionsResult> {
  const {
    machineID,
    ballType,
    createDataCollection,
    existingBtc,
    existingRt,
    forceRecreateBtc,
    forceRecreateRt,
  } = args;

  const [btc, rt] = await Promise.all([
    ensureCollection(
      DataCollectionType.BallTypeCalibration,
      machineID,
      ballType,
      existingBtc,
      createDataCollection,
      forceRecreateBtc
    ),
    ensureCollection(
      DataCollectionType.RepeatabilityTest,
      machineID,
      ballType,
      existingRt,
      createDataCollection,
      forceRecreateRt
    ),
  ]);

  return {
    btcID: btc.id,
    rtID: rt.id,
    recreatedBtc: btc.recreated,
    recreatedRt: rt.recreated,
  };
}

export interface ReconcileInstallationOnOpenArgs {
  installation: IInstallationDetails;
  machineID: string;
  btc?: IDataCollection;
  rt?: IDataCollection;
  createDataCollection: EnsureInstallCollectionsArgs['createDataCollection'];
  updateInstallation: (patch: Partial<IInstallationDetails>) => Promise<void>;
}

function collectionsMismatchInstallation(
  installation: IInstallationDetails,
  btc?: IDataCollection,
  rt?: IDataCollection
): boolean {
  return (
    (!!btc && btc.ball_type !== installation.ball_type) ||
    (!!rt && rt.ball_type !== installation.ball_type)
  );
}

async function anyCollectionHasShots(
  btc: IDataCollection | undefined,
  rt: IDataCollection | undefined
): Promise<boolean> {
  const checks: Promise<boolean>[] = [];

  if (btc?._id) {
    checks.push(
      DataCollectionsService.getShotSummaries(btc._id).then(collectionHasShots)
    );
  }

  if (rt?._id) {
    checks.push(
      DataCollectionsService.getShotSummaries(rt._id).then(collectionHasShots)
    );
  }

  if (!checks.length) {
    return false;
  }

  const results = await Promise.all(checks);
  return results.some(Boolean);
}

/**
 * Patches BTC/RT collections when installation.ball_type disagrees with saved
 * collections and neither step has started (no shots, not locked, no model).
 */
export async function reconcileInstallationOnOpen(
  args: ReconcileInstallationOnOpenArgs
): Promise<boolean> {
  const {
    installation,
    machineID,
    btc,
    rt,
    createDataCollection,
    updateInstallation,
  } = args;

  if (installation.standalone) {
    return false;
  }

  if (!collectionsMismatchInstallation(installation, btc, rt)) {
    return false;
  }

  if (installation.ball_type_locked || installation.model_id) {
    return false;
  }

  if (await anyCollectionHasShots(btc, rt)) {
    return false;
  }

  const result = await ensureInstallationDataCollections({
    machineID,
    ballType: installation.ball_type,
    installation,
    createDataCollection,
    existingBtc: btc,
    existingRt: rt,
  });

  const patch: Partial<IInstallationDetails> = {
    btcID: result.btcID,
    rtID: result.rtID,
  };

  if (result.recreatedBtc || result.recreatedRt) {
    patch.model_id = null;
  }

  await updateInstallation(patch);

  return true;
}
